import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RefreshCw, CheckCircle2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Kid-safe math CAPTCHA for public forms.
 * Renders a single-digit question and reports { token, answer, verified } via onChange.
 * The parent form is responsible for calling /api/captcha/math/verify (or the server-side helper)
 * before allowing submit. Also exposes an inline "Check" button for immediate feedback.
 */
export default function MathCaptcha({ onChange, size = 'sm' }) {
  const [question, setQuestion] = useState('');
  const [token, setToken] = useState('');
  const [answer, setAnswer] = useState('');
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const fetchNew = async () => {
    setBusy(true); setError(''); setVerified(false); setAnswer('');
    try {
      const res = await axios.get(`${API_URL}/api/captcha/math`);
      setQuestion(res.data.question);
      setToken(res.data.token);
      onChange?.({ token: res.data.token, answer: '', verified: false });
    } catch { setError('Could not load captcha'); }
    finally { setBusy(false); }
  };

  useEffect(() => { fetchNew(); /* eslint-disable-next-line */ }, []);

  const check = async () => {
    if (!answer.trim()) return;
    setBusy(true); setError('');
    try {
      const res = await axios.post(`${API_URL}/api/captcha/math/verify`, {
        token, answer: parseInt(answer, 10),
      });
      if (res.data?.success) {
        setVerified(true);
        onChange?.({ token, answer: parseInt(answer, 10), verified: true });
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Wrong answer — a new question is coming.');
      // Auto-refresh on wrong answer (single-use tokens)
      setTimeout(fetchNew, 800);
    } finally { setBusy(false); }
  };

  return (
    <div className={`border rounded-lg p-3 bg-slate-50 ${size === 'sm' ? 'text-sm' : ''}`} data-testid="math-captcha">
      <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Quick check — you&apos;re not a robot</p>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-lg font-semibold text-slate-800" data-testid="math-captcha-question">{question || '…'}</span>
        <Input
          type="number"
          value={answer}
          disabled={verified || busy}
          onChange={(e) => setAnswer(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); check(); } }}
          className="w-24 h-8 text-sm"
          placeholder="answer"
          data-testid="math-captcha-input"
        />
        {!verified ? (
          <>
            <Button type="button" size="sm" onClick={check} disabled={!answer || busy} className="h-8 bg-emerald-500 hover:bg-emerald-600" data-testid="math-captcha-check">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Check
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={fetchNew} disabled={busy} className="h-8" data-testid="math-captcha-refresh">
              <RefreshCw className="w-3 h-3" />
            </Button>
          </>
        ) : (
          <span className="text-emerald-700 text-xs flex items-center gap-1 font-semibold" data-testid="math-captcha-verified">
            <CheckCircle2 className="w-4 h-4" /> verified
          </span>
        )}
      </div>
      {error && <p className="text-red-600 text-xs mt-1" data-testid="math-captcha-error">{error}</p>}
    </div>
  );
}
